import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUp,
  Button,
  Header,
  IconButton,
  Leaderboard,
  Paragraph,
  RichTextDisplay,
  RotateClockWiseIcon,
  SwiperCard,
} from "@datagame/ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames/bind";

import { Game } from "../../../../../../gql/graphql";
import { isWordopolisGame } from "../../../../../../graphql/helpers/isWordopolisGame";
import { WordopolisPreview } from "../WordopolisPreview";
import DefaultBackground from "../../../../../../assets/images/cards/brown.svg";
import { isLeaderboardGame } from "../../../../../../graphql/helpers/LeaderboardGame";
import { Step } from "../Step6Preview/Content/Content";
import { STEPS_TO_CONFIGURE_PROJECT } from "../GamePreview";
import { LEADERBOARD_TEST_RESULTS } from "../../../../../../constants";

import styles from "./SharedPreview.module.scss";
import { isCardBasedGame } from "../../../../../../graphql/helpers/CardBasedGame";

const STEPS = {
  cards: "Cards",
  wordopolis: "Wordopolis",
  leaderboard: "Leaderboard",
};

const cx = classNames.bind(styles);

type SharedPreviewProps = {
  game: Game;
  isFlickable?: boolean;
  step?: STEPS_TO_CONFIGURE_PROJECT;
};

export const SharedPreview = ({ game, isFlickable, step }: SharedPreviewProps) => {
  const defaultStep = isWordopolisGame(game) ? STEPS.wordopolis : STEPS.cards;

  const { t } = useTranslation("project");
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentCard, setCurrentCard] = useState(0);
  const [isBackgroundCard, setIsBackgroundCard] = useState(false);
  const [activeStep, setActiveStep] = useState(defaultStep);
  const isWordopolis = isWordopolisGame(game);

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleChangeCard = (nextCardIndex: number) => {
    setCurrentCard((prev) => prev + nextCardIndex);
  };

  const handleFlipCard = () => {
    setIsBackgroundCard((prev) => !prev);
  };

  const showLeaderboardStep = useMemo(() => {
    return isLeaderboardGame(game) && game.configuration.showLeaderboard;
  }, [isLeaderboardGame(game) && game.configuration.showLeaderboard]);

  const renderPlaceholder = () => (
    <>
      <Header variant="h3" className={styles.placeholderTitle}>
        {t("preview.cardsPlaceholder")}
      </Header>
      <span className={styles.placeholder}>{t(`preview.bodyPlaceholder.${isWordopolis ? "word" : "card"}`)}</span>
    </>
  );

  const attributes = useMemo(() => {
    if (isCardBasedGame(game)) {
      return game.configuration.attributes;
    }

    return null;
  }, [game.configuration, game.kind]);

  const getQuestion = () => {
    if ("mostQuestion" in game.configuration && "leastQuestion" in game.configuration) {
      if (game.configuration.mostQuestion === "" && game.configuration.leastQuestion == "") {
        return "";
      }

      return (
        <>
          <RichTextDisplay value={game.configuration.mostQuestion} />
          {" / "}
          <RichTextDisplay value={game.configuration.leastQuestion} />
        </>
      );
    }

    if ("question" in game.configuration) {
      return <RichTextDisplay value={game.configuration.question} />;
    }
    return "";
  };

  const renderCards = () => {
    if (!attributes?.length) {
      return renderPlaceholder();
    }

    const cardBackImageUrl = game.configuration.gameVisuals.cardBackImage?.url ?? DefaultBackground;

    return (
      <>
        <div className={styles.cardsWrapper}>
          <IconButton
            size="small"
            icon={<ArrowLeftIcon />}
            onClick={() => handleChangeCard(-1)}
            disabled={currentCard === 0}
          />
          <div className={cx("cardWrapper", { isBackgroundCard })}>
            <SwiperCard
              className={styles.card}
              name={attributes?.[currentCard].name}
              customText={attributes?.[currentCard].customText}
              showImageAndText={attributes?.[currentCard].showImageAndText}
              isFlipped={isBackgroundCard}
              imageUrl={isBackgroundCard ? cardBackImageUrl : attributes?.[currentCard].image?.url}
            >
              {isFlickable && (
                <Button
                  size="small"
                  icon={<RotateClockWiseIcon />}
                  iconSide="left"
                  className={styles.flipButton}
                  onClick={handleFlipCard}
                >
                  {t("preview.flipCard")}
                </Button>
              )}
            </SwiperCard>
          </div>

          <IconButton
            size="small"
            icon={<ArrowRightIcon />}
            onClick={() => handleChangeCard(1)}
            disabled={currentCard === attributes.length - 1}
          />
        </div>
        <Header variant="h6" className={styles.previewBodyCounter}>
          {currentCard + 1} / {attributes.length}
        </Header>
      </>
    );
  };

  const renderSteps = () => {
    if (showLeaderboardStep && step === STEPS_TO_CONFIGURE_PROJECT.GAME_SETTINGS) {
      return (
        <div className={styles.tabs}>
          <Step step={defaultStep} activeStep={activeStep} onClick={setActiveStep} />

          <Step step={STEPS.leaderboard} activeStep={activeStep} onClick={setActiveStep} />
        </div>
      );
    }
  };

  const renderActiveStepContent = () => {
    if (activeStep === STEPS.leaderboard && showLeaderboardStep && isLeaderboardGame(game)) {
      const toShow = game.configuration.leaderboardConfiguration?.numberOfPlayers;

      return (
        <Leaderboard
          className={styles.leaderboard}
          configuration={game.configuration.leaderboardConfiguration}
          results={LEADERBOARD_TEST_RESULTS.slice(0, toShow)}
        />
      );
    }

    if (isWordopolisGame(game)) {
      return <WordopolisPreview game={game} />;
    }

    return (
      <>
        <Paragraph weight="font-bold" className={styles.previewBodyTittle}>
          {getQuestion()}{" "}
        </Paragraph>
        {renderCards()}
      </>
    );
  };

  const wrapperClassNames = cx("wrapper", { isExpanded });

  return (
    <div className={wrapperClassNames}>
      <Header variant="h5" className={styles.previewHeaderMobile}>
        <span className={styles.placeholderHeader}>{t("preview.titlePlaceholder")}</span>
        <IconButton size="small" icon={isExpanded ? <ArrowUp /> : <ArrowDownIcon />} onClick={toggleExpanded} />
      </Header>

      <div className={cx("previewBody", { withMargin: !showLeaderboardStep })}>
        {renderSteps()}
        {renderActiveStepContent()}
      </div>
    </div>
  );
};
